import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../apiClient";
import Confetti from "react-confetti";
import "./LoginPage.css";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../config/msalConfig";
import { getDefaultRouteForRole } from "../../../utils/page-permissions";
import { captureReturnUrlFromLocation, consumeReturnUrl, markLoggedIn } from "../../../utils/auth";

function LoginPage() {
  const [errorMessage, setErrorMessage] = useState("");
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [randomEmoji, setRandomEmoji] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Use ref to prevent multiple simultaneous processing attempts
  const isProcessingRef = useRef(false);
  const hasProcessedAccountRef = useRef(false);

  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const emojis = React.useMemo(() => ["🎉", "🚀", "✨", "🎊"], []);

  // Check if already authenticated on mount
  useEffect(() => {
    // Stash any ?returnUrl= now so it survives the SSO redirect round-trip
    // (loginRedirect leaves the page and returns to "/" with no query string).
    captureReturnUrlFromLocation();
    const token = localStorage.getItem("authToken");
    if (token) {
      const userRole = localStorage.getItem("userRole");
      // Honor a pending deep-link target if present, else the role home page.
      const dest = consumeReturnUrl() || getDefaultRouteForRole(userRole);
      console.log("🔑 Already authenticated, redirecting to", dest);
      navigate(dest);
    }
  }, [navigate]);

  // Process Microsoft login response (for both popup and redirect)
  const processMicrosoftAccount = useCallback(async (account, idToken = null) => {
    // Use ref for synchronous check to prevent race conditions
    if (isProcessingRef.current || localStorage.getItem("authToken")) {
      console.log("⏭️ Skipping - already processing or authenticated");
      return;
    }

    // Set ref immediately (synchronous) to block other calls
    isProcessingRef.current = true;
    hasProcessedAccountRef.current = true;
    setIsProcessing(true);

    try {
      const email = account?.username ||
        account?.idTokenClaims?.preferred_username ||
        account?.idTokenClaims?.email;

      if (!email) {
        console.error("❌ No email found in Microsoft account");
        setErrorMessage("Unable to determine email from Microsoft account.");
        return;
      }

      // Email redacted from logs for security

      // Use provided token or acquire one silently
      let microsoftToken = idToken;
      if (!microsoftToken) {
        try {
          const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account: account
          });
          microsoftToken = tokenResponse.idToken;
        } catch (tokenError) {
          console.error("❌ Token acquisition failed:", tokenError);
          setErrorMessage("Unable to retrieve Microsoft token.");
          return;
        }
      }

      if (!microsoftToken) {
        console.error("❌ No token found");
        setErrorMessage("Unable to retrieve Microsoft token.");
        return;
      }

      // Token validation in progress

      const { data } = await apiClient.post("/api/login/microsoft", {
        email,
        microsoftToken
      });

      if (data.message === "Login successful") {
        console.log("✅ Backend validation successful");

        const userRole = data.role?.toLowerCase() ?? "";
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("userRole", userRole);
        localStorage.setItem("userEmail", data.email ?? "");
        localStorage.setItem("userId", data.userId ?? "");
        localStorage.setItem("userName", data.name ?? "");
        localStorage.setItem("lastActivity", Date.now().toString());
        markLoggedIn();

        setLoginSuccess(true);
        setRandomEmoji(emojis[Math.floor(Math.random() * emojis.length)]);
        setShowConfetti(true);

        // Honor a pending deep-link target (e.g. an email "See Full Detail"
        // button that bounced through login) if present, else the role home.
        const dest = consumeReturnUrl() || getDefaultRouteForRole(userRole);
        setTimeout(() => {
          setShowConfetti(false);
          navigate(dest);
        }, 2500);
      }
    } catch (error) {
      console.error("❌ Error processing Microsoft account:", error);

      // IMPORTANT: Do NOT reset hasProcessedAccountRef - this prevents infinite retry loops
      // Only reset isProcessingRef to allow manual retry via button click
      isProcessingRef.current = false;
      // hasProcessedAccountRef stays TRUE to prevent automatic retry from useEffect

      if (error.response?.status === 400 || error.response?.status === 401) {
        setErrorMessage(error.response?.data?.error || "Your account is not authorized. Please contact HR.");
      } else if (error.response?.status === 403) {
        setErrorMessage(error.response?.data?.error || "Access denied. You don't have an assigned role in the system.");
      } else if (error.response?.status === 503) {
        setErrorMessage(error.response?.data?.error || "Service temporarily unavailable. Please try again later.");
      } else {
        setErrorMessage("Microsoft login failed. Please try again.");
      }

      // Sign out from MSAL to clear the cached account and prevent auto-retry
      try {
        await instance.clearCache();
        console.log("🧹 Cleared MSAL cache after failed login");
      } catch (clearError) {
        console.warn("Could not clear MSAL cache:", clearError);
      }
    } finally {
      setIsProcessing(false);
    }
  }, [instance, navigate, emojis]);

  // Handle redirect response when returning from Microsoft
  useEffect(() => {
    // Skip if already processing or authenticated
    if (inProgress !== "none" || localStorage.getItem("authToken") || isProcessingRef.current) {
      return;
    }

    instance.handleRedirectPromise()
      .then((response) => {
        if (response && response.account && !isProcessingRef.current) {
          console.log("✅ Redirect login completed, processing account...");
          processMicrosoftAccount(response.account, response.idToken);
        }
      })
      .catch((error) => {
        console.error("Redirect error:", error);
        if (error.errorCode !== "no_cached_authority") {
          setErrorMessage("Microsoft login failed. Please try again.");
        }
      });
  }, [instance, inProgress, processMicrosoftAccount]);

  // Check if already logged in via Microsoft (e.g., after redirect)
  useEffect(() => {
    // Only process if: has accounts, no auth token, not currently processing, and hasn't already been processed
    if (
      accounts.length > 0 &&
      !localStorage.getItem("authToken") &&
      inProgress === "none" &&
      !isProcessingRef.current &&
      !hasProcessedAccountRef.current
    ) {
      console.log("📋 Found existing Microsoft account, processing...");
      processMicrosoftAccount(accounts[0]);
    }
  }, [accounts, inProgress, processMicrosoftAccount]);

  const handleMicrosoftLogin = async () => {
    setErrorMessage("");

    // Prevent double-clicks
    if (isProcessing || inProgress !== "none") {
      console.log("⏭️ Login already in progress, skipping...");
      return;
    }

    // Reset the processed flag when user manually clicks - this allows retry
    hasProcessedAccountRef.current = false;

    try {
      console.log("🔵 Initiating Microsoft login...");

      // Try popup first, fall back to redirect if blocked
      let loginResponse;
      try {
        loginResponse = await instance.loginPopup({
          ...loginRequest,
          prompt: "select_account"
        });
        console.log("✅ Microsoft popup completed");

        // Process the popup response immediately with the idToken
        if (loginResponse?.account) {
          await processMicrosoftAccount(loginResponse.account, loginResponse.idToken);
        }
      } catch (popupError) {
        // If popup is blocked or COOP issues, use redirect
        if (popupError.errorCode === "popup_window_error" ||
            popupError.errorCode === "empty_window_error" ||
            popupError.message?.includes("Cross-Origin-Opener-Policy")) {
          console.log("⚠️ Popup blocked, using redirect...");
          await instance.loginRedirect(loginRequest);
          return; // Redirect will reload the page
        }
        throw popupError;
      }
    } catch (error) {
      console.error("❌ Microsoft login error:", error);

      if (error.errorCode === "user_cancelled") {
        setErrorMessage("Microsoft login was cancelled.");
      } else if (error.response?.status === 400 || error.response?.status === 401) {
        setErrorMessage(error.response?.data?.error || "Your account is not authorized. Please contact HR.");
      } else if (error.response?.status === 403) {
        setErrorMessage(error.response?.data?.error || "Access denied. You don't have an assigned role in the system.");
      } else if (error.response?.status === 503) {
        setErrorMessage(error.response?.data?.error || "Service temporarily unavailable. Please try again later.");
      } else {
        setErrorMessage("Microsoft login failed. Please try again.");
      }
    }
  };

  // Show loading state while processing Microsoft login
  if (isProcessing || (inProgress !== "none" && inProgress !== "startup")) {
    return (
      <div className="login-page">
        <div className="success-container">
          <div className="success-card">
            <div className="success-emoji">🔐</div>
            <h1 className="success-title">Authenticating...</h1>
            <p className="success-subtitle">Please wait while we verify your account</p>
            <div className="success-loader">
              <div className="loader-bar"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      {showConfetti && <Confetti />}

      {loginSuccess ? (
        <div className="success-container">
          <div className="success-card">
            <div className="success-emoji">{randomEmoji}</div>
            <h1 className="success-title">Welcome Back!</h1>
            <p className="success-subtitle">Login Successful</p>
            <div className="success-loader">
              <div className="loader-bar"></div>
            </div>
            <p className="success-redirect">Redirecting to dashboard...</p>
          </div>
        </div>
      ) : (
        <div className="login-container">
          <h2 className="welcome-message">Welcome to People Plan</h2>

          {errorMessage && (
            <div className="error-message-box">
              <p className="error-message">{errorMessage}</p>
            </div>
          )}

          <div className="microsoft-login-section">
            <button
              onClick={handleMicrosoftLogin}
              className="microsoft-login-button"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                alt="Microsoft logo"
              />
              Login with Microsoft
            </button>
          </div>

          <div className="login-footer">
            <p className="support-message">
              If you need further support, please submit a ticket in the&nbsp;
              <a
                href="https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=c20lHhPoUEuR7EvYBL3H8ewN9uQ3mkFIu3AQhhHae69UMkxSQUdWTDcwVzFYTUJRQllBSjBRTjhVVS4u"
                target="_blank"
                rel="noopener noreferrer"
                className="support-link"
              >
                <strong>HR Requests Ticketing System</strong>
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginPage;